import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, BACKEND_URL } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Menu, Upload, ImageIcon, Search, Check, X, Loader2 } from "lucide-react";

export default function AdminPhotos() {
  const { authAxios } = useAuth();
  const [products, setProducts] = useState([]);
  const [uploads, setUploads] = useState([]); // {id, file, preview, uploading, uploaded, url, productId, productSearch, showDropdown}
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await authAxios.get("/products");
      setProducts(res.data);
    } catch {
      toast.error("Errore nel caricamento prodotti");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleFiles = (files) => {
    const newUploads = Array.from(files)
      .filter(f => f.type.startsWith("image/"))
      .map((file, i) => ({
        id: Date.now() + i,
        file,
        preview: URL.createObjectURL(file),
        uploading: false,
        uploaded: false,
        url: null,
        productId: null,
        productSearch: "",
        showDropdown: false,
      }));
    if (newUploads.length === 0) {
      toast.error("Nessuna immagine selezionata");
      return;
    }
    setUploads(prev => [...prev, ...newUploads]);
    toast.success(`${newUploads.length} immagini aggiunte`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const removeUpload = (id) => {
    setUploads(prev => prev.filter(u => u.id !== id));
  };

  const updateUpload = (id, data) => {
    setUploads(prev => prev.map(u => u.id === id ? { ...u, ...data } : u));
  };

  const selectProduct = (uploadId, product) => {
    updateUpload(uploadId, {
      productId: product.id,
      productSearch: product.name,
      showDropdown: false,
    });
  };

  const uploadAndAssign = async (upload) => {
    if (!upload.productId) {
      toast.error("Seleziona un prodotto prima");
      return;
    }
    updateUpload(upload.id, { uploading: true });
    try {
      // Upload image
      const formData = new FormData();
      formData.append("file", upload.file);
      const res = await authAxios.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `${BACKEND_URL}${res.data.url}`;

      // Assign to product
      await authAxios.put(`/products/${upload.productId}`, { image_url: imageUrl });

      updateUpload(upload.id, { uploading: false, uploaded: true, url: imageUrl });
      toast.success("Foto caricata e associata");
    } catch (e) {
      updateUpload(upload.id, { uploading: false });
      toast.error("Errore nel caricamento");
    }
  };

  const uploadAll = async () => {
    const pending = uploads.filter(u => !u.uploaded && u.productId);
    if (pending.length === 0) {
      toast.error("Nessuna foto pronta da caricare. Associa prima i prodotti.");
      return;
    }
    for (const upload of pending) {
      await uploadAndAssign(upload);
    }
    toast.success(`${pending.length} foto caricate!`);
  };

  const pendingCount = uploads.filter(u => !u.uploaded && u.productId).length;
  const uploadedCount = uploads.filter(u => u.uploaded).length;
  const unassignedCount = uploads.filter(u => !u.uploaded && !u.productId).length;

  const getFilteredProducts = (search) => {
    if (!search) return products.slice(0, 15);
    const q = search.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)
    ).slice(0, 10);
  };

  return (
    <div className="min-h-screen bg-[#09090b]">

      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Foto" />
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Foto Prodotti</h1>
              <p className="text-zinc-400 text-sm">Carica foto in blocco e associale ai prodotti</p>
            </div>
          </div>
          {pendingCount > 0 && (
            <Button
              data-testid="upload-all-btn"
              onClick={uploadAll}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              <Upload className="w-4 h-4 mr-2" />
              Carica tutte ({pendingCount})
            </Button>
          )}
        </div>

        {/* Stats */}
        {uploads.length > 0 && (
          <div className="flex gap-4 mb-6 text-sm">
            <span className="text-zinc-500">{uploads.length} totali</span>
            {unassignedCount > 0 && <span className="text-amber-400">{unassignedCount} da associare</span>}
            {pendingCount > 0 && <span className="text-blue-400">{pendingCount} pronte</span>}
            {uploadedCount > 0 && <span className="text-emerald-400">{uploadedCount} caricate</span>}
          </div>
        )}

        {/* Drop Zone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
          className="mb-8 border-2 border-dashed border-white/10 hover:border-amber-500/40 rounded-xl p-10 text-center cursor-pointer transition-colors"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="bulk-file-input"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <ImageIcon className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
          <p className="text-zinc-300 font-medium mb-1">Trascina le foto qui o clicca per selezionare</p>
          <p className="text-zinc-500 text-sm">JPG, PNG, WEBP - Puoi selezionare più file</p>
        </div>

        {/* Upload Items */}
        {uploads.length > 0 && (
          <div className="space-y-3">
            {uploads.map((upload) => (
              <Card key={upload.id} data-testid={`upload-item-${upload.id}`} className={`bg-[#18181b] border-white/5 ${upload.uploaded ? 'border-emerald-500/20' : ''}`}>
                <CardContent className="p-3 flex items-center gap-4">
                  {/* Thumbnail */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#27272a] flex-shrink-0">
                    <img src={upload.preview} alt="" className="w-full h-full object-cover" />
                  </div>

                  {/* File name */}
                  <div className="flex-shrink-0 w-36">
                    <p className="text-white text-sm font-medium truncate">{upload.file.name}</p>
                    <p className="text-zinc-500 text-xs">{(upload.file.size / 1024).toFixed(0)} KB</p>
                  </div>

                  {/* Product Selector */}
                  <div className="flex-1 relative">
                    {upload.uploaded ? (
                      <div className="flex items-center gap-2 text-emerald-400">
                        <Check className="w-4 h-4" />
                        <span className="text-sm">{upload.productSearch}</span>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                          <Input
                            data-testid={`product-search-${upload.id}`}
                            type="text"
                            placeholder="Cerca prodotto per nome o SKU..."
                            value={upload.productSearch}
                            onChange={(e) => updateUpload(upload.id, { productSearch: e.target.value, showDropdown: true, productId: null })}
                            onFocus={() => updateUpload(upload.id, { showDropdown: true })}
                            className="pl-8 h-9 bg-[#27272a] border-white/10 text-sm"
                          />
                        </div>
                        {upload.showDropdown && (
                          <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-[#27272a] border border-white/10 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                            {getFilteredProducts(upload.productSearch).map(p => (
                              <button
                                key={p.id}
                                onClick={() => selectProduct(upload.id, p)}
                                className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2 transition-colors"
                              >
                                <div className="w-8 h-8 rounded bg-[#18181b] overflow-hidden flex-shrink-0">
                                  {p.image_url ? (
                                    <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <ImageIcon className="w-3 h-3 text-zinc-600" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-xs truncate">{p.name}</p>
                                  <p className="text-zinc-500 text-[10px] font-mono">{p.sku}</p>
                                </div>
                              </button>
                            ))}
                            {getFilteredProducts(upload.productSearch).length === 0 && (
                              <p className="text-zinc-500 text-xs p-3 text-center">Nessun prodotto trovato</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!upload.uploaded && (
                      <>
                        <Button
                          data-testid={`upload-single-${upload.id}`}
                          size="sm"
                          onClick={() => uploadAndAssign(upload)}
                          disabled={!upload.productId || upload.uploading}
                          className="bg-amber-500 hover:bg-amber-600 text-black text-xs h-8"
                        >
                          {upload.uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUpload(upload.id)}
                          className="text-zinc-400 hover:text-red-400 h-8"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                    {upload.uploaded && (
                      <span className="text-emerald-400 text-xs font-medium">Fatto</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty state */}
        {uploads.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-zinc-500 text-sm">Seleziona le foto per iniziare</p>
          </div>
        )}
      </main>
    </div>
  );
}
